/**
 * Briefing Export Component
 * Export controls for executive briefings
 * Part 3, Phase 5 - Executive Briefing UI
 *
 * Features:
 * - PDF export button
 * - DOCX export button
 * - Download progress tracking
 * - Error handling
 */

import React, { useState } from 'react';
import { Modal, Button, Alert, Progress, Radio } from 'antd';
import {
  FilePdfOutlined,
  FileWordOutlined,
  DownloadOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import execute from '../../../hooks/execute';
import { protectedResources } from '../../../config/msalConfig';

const BriefingExport = ({ visible, onClose, briefingId, briefingTitle }) => {
  const [format, setFormat] = useState('PDF');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Handle export
   */
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      setSuccess(false);

      const endpoint = `${protectedResources.api.baseEndPoint}/v1/executive/briefing/${briefingId}/export`;

      // Make request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAccessToken()}`
        },
        body: JSON.stringify({ format })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Export failed');
      }

      // Get blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${briefingTitle || 'Executive_Briefing'}_${Date.now()}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('[BriefingExport] Export failed:', err);
      setError(err.message || 'Failed to export briefing');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Get access token for fetch request
   */
  const getAccessToken = async () => {
    const { msalInstance } = await import('../../../index');
    const account = msalInstance.getActiveAccount();
    const result = await msalInstance.acquireTokenSilent({
      scopes: protectedResources.api.scopes.read,
      account: account,
      redirectUri: '/'
    });
    return result.accessToken;
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!exporting) {
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Modal
      title="Export Executive Briefing"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={format === 'PDF' ? <FilePdfOutlined /> : <FileWordOutlined />}
          onClick={handleExport}
          loading={exporting}
          disabled={success}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      ]}
    >
      {/* Format Selection */}
      <div className="mb-6">
        <div className="mb-3 font-medium">Select Export Format:</div>
        <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)} disabled={exporting}>
          <Radio.Button value="PDF">
            <FilePdfOutlined className="mr-2" />
            PDF Document
          </Radio.Button>
          <Radio.Button value="DOCX">
            <FileWordOutlined className="mr-2" />
            Word Document
          </Radio.Button>
        </Radio.Group>
      </div>

      {/* Export Progress */}
      {exporting && (
        <div className="mb-4">
          <div className="mb-2 text-gray-600">Generating {format} file...</div>
          <Progress percent={100} status="active" showInfo={false} />
        </div>
      )}

      {/* Success Message */}
      {success && (
        <Alert
          message="Export Successful"
          description={`Your briefing has been exported as ${format}. The download should start automatically.`}
          type="success"
          icon={<CheckCircleOutlined />}
          showIcon
          className="mb-4"
        />
      )}

      {/* Error Message */}
      {error && (
        <Alert
          message="Export Failed"
          description={error}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Format Info */}
      {!exporting && !success && !error && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <div className="text-sm text-gray-700">
            <div className="font-medium mb-2">Export Details:</div>
            <ul className="list-disc list-inside space-y-1">
              <li>Professional template with organization branding</li>
              <li>Includes all metrics, trends, and recommendations</li>
              <li>Charts and graphs embedded as images</li>
              <li>Table of contents automatically generated</li>
              <li>Optimized for printing and sharing</li>
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BriefingExport;
